import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Data Alchemist Bootcamp\npageTitle: Data Alchemist Bootcamp | Increment | Covalent\n---\n\n# {% $markdoc.frontmatter.title %}\n\n## A school in the browser: no location, no schedule and no barrier to entry!\n\nWeb3's influence on the global economy is growing exponentially, deepening the demand for on-chain business intelligence and analysis skills. Yet, there is a serious deficit in Web3 Data Analysts despite the role being identified as one of the fastest-growing entry-level jobs in today's labor market.\n\nIn response to this talent shortage, Covalent has launched the *Data Alchemist Boot-Camp*, aimed at bridging Web3 skill gaps for workers seeking employment in the fast-growing blockchain industry.\n\nAll industries using big data need people that understand how to use data to drive operational decisions, key customer insights, and strategic initiatives. The availability of Web3 data-driven roles will increase as Web3 companies start to understand the strategic value of having visibility and insight with their on-chain data. Their work will encompass analyses across multiple blockchains, protocols, and user types to create custom metrics that enlighten the reach, retention, and revenue areas of their companies. Therefore, the Data Alchemist Boot-Camp aims to foster candidates' skills in visualizing and storytelling with Web3 data across the {% chainCount /%} chains Covalent has indexed.\n\nAt Covalent, we plan on scaling this educational initiative to a 1,000 Web3 Data Analysts. There will be a new cohort enrolled every couple of months - so don't get disheartened if you aren't accepted in the first cohort.\n\n- **Week 1** – Introduction to blockchain data and business intelligence.\n- **Week 2** – Reach, Retention & Revenue (RRR) framework for Web3 analysis.\n- **Week 3** – Build multi-chain analysis across 200+ chains with a standardized schema.\n- **Week 4** – Learning how to utilize AI and LLMs to supercharge your analysis.\n\n{% discordcta /%}\n\n\n### Quote from Ganesh Swami, CEO of Covalent:\n                \"*The only prerequisite to joining our Data Alchemist Boot-Camp is a desire to learn about Web3; come with that, and we'll pay you to learn. There is a real need for individuals who are experts in this new cross-section of data and blockchain. We want to help train these future leaders to quickly fill these new roles in the near term. This boot-camp is how we try to spark change in an excited and rapidly growing community.*\"\n\n\n\n## Alchemist Testimonies\n{% youtube videoId=\"ntKOX0KMNRk?t=949\" /%}\n\n{% twitter tweetId=\"1600076530448617473\" /%}\n\n{% twitter tweetId=\"1599960161392594946\" /%}\n\n{% twitter tweetId=\"1600002219436232705\" /%}\n\n{% discordcta /%}";
const filepath = "/docs/increment/data-bootcamp/data-alchemist-introduction.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
